import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import classes from '../NavBar.module.scss'

import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { Icon, Menu } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { permission, scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { useMatchingBudget } from '@percent/workplace-giving/common/hooks'
import { Permissions } from '@percent/workplace-giving/constants/permissions'

export function ActionsMenu() {
  const { track } = useAnalytics()
  const navigation = useNavigate()
  const { t } = useTranslation()
  const has = usePermission()
  const { data: matching } = useMatchingBudget()

  const menuConfig = [
    {
      sectionKey: 'donation',
      items: [
        {
          key: 'submit-receipt',
          label: t('workplace_giving.navigation.submitReceipt'),
          condition: () =>
            has(scope('donation_matching'), scope('off_platform_matching')) &&
            (matching?.remainingAmount?.amount ?? 0) > 0,
          action: () => {
            track({
              event: 'Donation Match Request Start',
              properties: { origin: 'Main header' }
            })
            navigation(AppRoute.CREATE_MATCH_REQUEST)
          }
        },
        {
          key: 'create-fundraiser',
          label: t('workplace_giving.navigation.createFundraiser'),
          condition: () => has(scope('donation_link'), permission(Permissions.FundraiserWrite)),
          action: () => {
            track({
              event: 'Donation Fundraiser Create Start',
              properties: { origin: 'Main header' }
            })
            navigation(AppRoute.CREATE_FUNDRAISER)
          }
        }
      ]
    },
    {
      sectionKey: 'volunteering',
      items: [
        {
          key: 'log-time',
          label: t('workplace_giving.navigation.logTime'),
          condition: () => has(scope('volunteering')),
          action: () => {
            track({
              event: 'Volunteering Time Logging Start',
              properties: { origin: 'Main header' }
            })
            navigation(AppRoute.LOG_VOLUNTEERING_ACTIVITY)
          }
        },
        {
          key: 'create-opportunity',
          label: t('workplace_giving.navigation.createOpportunity'),
          condition: () => has(scope('volunteering')) && has(permission(Permissions.OpportunityWrite)),
          action: () => {
            track({
              event: 'Volunteering Opportunity Create Start',
              properties: { origin: 'Main header' }
            })
            navigation(AppRoute.CREATE_OPPORTUNITY)
          }
        }
      ]
    },
    {
      sectionKey: 'campaign',
      items: [
        {
          key: 'create-campaign',
          label: t('workplace_giving.navigation.createCampaign'),
          condition: () => has(permission(Permissions.CampaignsWrite)) && has(scope('giving_campaign')),
          action: () => {
            track({
              event: 'Giving Campaign Create Start',
              properties: { origin: 'Main header' }
            })
            navigation(AppRoute.CREATE_CAMPAIGN)
          }
        }
      ]
    }
  ]

  const sections = menuConfig.map(section => ({
    items: section.items
      .filter(item => item.condition())
      .map(item => ({
        key: item.key,
        label: item.label,
        as: 'button' as const,
        onClick: item.action
      }))
  }))

  // The wrapper positions the dropdown, so it has to go too — left behind it is an
  // empty flex child that still draws the row's gap.
  if (sections.every(section => section.items.length === 0)) {
    return null
  }

  return (
    <div className={classes.actionsMenu}>
      <Menu
        customTriggerComponent={
          <button
            aria-label={t('workplace_giving.common.more')}
            className={classes.actionIconButton}
            data-testid="actions-menu"
            type="button"
          >
            <Icon name="add" color="white" size={6} />
          </button>
        }
        sections={sections}
      />
    </div>
  )
}
